import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, GripVertical, Eye, EyeOff } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    icon: '',
    order: 0,
    is_active: true,
    category: 'entretien',
    eligible_credit_impot: true,
    features: []
  });
  const [featuresInput, setFeaturesInput] = useState('');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?active_only=false`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        title: service.title,
        description: service.description,
        image_url: service.image_url,
        icon: service.icon || '',
        order: service.order,
        is_active: service.is_active,
        category: service.category || 'entretien',
        eligible_credit_impot: service.eligible_credit_impot !== false,
        features: service.features || []
      });
      setFeaturesInput((service.features || []).join('\n'));
    } else {
      setEditingService(null);
      setFormData({
        title: '',
        description: '',
        image_url: '',
        icon: '',
        order: services.length,
        is_active: true,
        category: 'entretien',
        eligible_credit_impot: true,
        features: []
      });
      setFeaturesInput('');
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, formData);
        toast.success('Service mis à jour');
      } else {
        await axios.post(`${API}/services`, formData);
        toast.success('Service créé');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) return;
    
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success('Service supprimé');
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (service) => {
    try {
      await axios.put(`${API}/services/${service.id}`, { is_active: !service.is_active });
      fetchServices();
      toast.success(service.is_active ? 'Service désactivé' : 'Service activé');
    } catch (error) {
      console.error('Error toggling service:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  return (
    <AdminLayout title="Gestion des Services">
      <div className="space-y-6" data-testid="admin-services">
        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-stone-500">
            {services.length} service{services.length > 1 ? 's' : ''}
          </p>
          <Button onClick={() => openDialog()} className="btn-primary" data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un service
          </Button>
        </div>

        {/* Services List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="divide-y divide-stone-100">
              {services.map((service) => (
                <div 
                  key={service.id} 
                  className={`p-4 flex items-center gap-4 ${!service.is_active ? 'opacity-50' : ''}`}
                  data-testid={`service-item-${service.id}`}
                >
                  <GripVertical className="w-5 h-5 text-stone-300 cursor-grab" />
                  <img 
                    src={service.image_url} 
                    alt={service.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-stone-900">{service.title}</h3>
                      {service.eligible_credit_impot !== false ? (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">50% crédit impôt</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">Sur devis</span>
                      )}
                    </div>
                    <p className="text-stone-500 text-sm truncate">{service.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleActive(service)}
                      className={`p-2 rounded-lg transition-colors ${
                        service.is_active 
                          ? 'text-emerald-600 hover:bg-emerald-50' 
                          : 'text-stone-400 hover:bg-stone-100'
                      }`}
                      title={service.is_active ? 'Désactiver' : 'Activer'}
                      data-testid={`toggle-service-${service.id}`}
                    >
                      {service.is_active ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => openDialog(service)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      data-testid={`edit-service-${service.id}`}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`delete-service-${service.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
              {services.length === 0 && (
                <div className="p-12 text-center text-stone-500">
                  Aucun service. Cliquez sur "Ajouter un service" pour commencer.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingService ? 'Modifier le service' : 'Ajouter un service'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="service-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="service-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image_url">URL de l'image</Label>
                <Input
                  id="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  placeholder="https://..."
                  data-testid="service-image-input"
                />
                {formData.image_url && (
                  <img 
                    src={formData.image_url} 
                    alt="Preview" 
                    className="w-full h-32 object-cover rounded-lg mt-2"
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="icon">Icône (optionnel)</Label>
                  <Input
                    id="icon"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="scissors, leaf..."
                    data-testid="service-icon-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order">Ordre</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    data-testid="service-order-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="service-active-switch"
                />
                <Label htmlFor="is_active">Service actif</Label>
              </div>

              {/* Category Selection */}
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="entretien">Entretien (avec avance immédiate)</option>
                  <option value="conseil">Conseil & Études (sur devis)</option>
                  <option value="amenagement">Aménagement (sur devis)</option>
                </select>
              </div>

              {/* Credit d'impot eligibility */}
              <div className="flex items-center gap-2">
                <Switch
                  id="eligible_credit_impot"
                  checked={formData.eligible_credit_impot}
                  onCheckedChange={(checked) => setFormData({ ...formData, eligible_credit_impot: checked })}
                />
                <Label htmlFor="eligible_credit_impot">Éligible au crédit d'impôt 50%</Label>
              </div>

              {/* Features */}
              <div className="space-y-2">
                <Label htmlFor="features">Caractéristiques (une par ligne)</Label>
                <Textarea
                  id="features"
                  value={featuresInput}
                  onChange={(e) => {
                    setFeaturesInput(e.target.value);
                    setFormData({ 
                      ...formData, 
                      features: e.target.value.split('\n').filter(f => f.trim()) 
                    });
                  }}
                  placeholder="Tonte régulière&#10;Ramassage inclus&#10;Bordures soignées"
                  rows={4}
                />
                <p className="text-xs text-stone-500">Ces caractéristiques s'afficheront sur la page services</p>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="btn-primary" data-testid="save-service-btn">
                  {editingService ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminServices;
